import { CUSTOM_ELEMENTS_SCHEMA, DoBootstrap, inject, Injector, NgModule, provideAppInitializer } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { BrowserModule } from '@angular/platform-browser';
import { CoreModule } from '@core/core.module';
import { PrimeNGConfigLoader, providePrimeNGFromShell, RouteInterceptor, ShellService, TokenInterceptor } from '@mynexus/mfe-core';
import { MncNotesComponent } from './mnc-notes.component';
import { ConfigurationService } from './core/configuration/configuration.service';
import { FeatureToggleModule, SecurityModule, SecurityService, UtcDateWithoutTimeZonePipe, UtcToLocalPipe } from '@mynexus/mynexus-ui-lib';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { environment } from '@env/environment';
import { IConfiguration } from './core/configuration/IConfiguration';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { MncNotesRoutingModule } from './mnc-notes-routing.module';
import { BroadcastServiceComponent } from './shared/broadcast-service/broadcast-service.component';
import { NotesServiceComponent } from './shared/notes-service/notes-service.component';
import { NoteMfeService } from './core/service/note-mfe.service';
import { NotesSidebarComponent } from './shared/notes-sidebar/notes-sidebar.component';
import { NoteWidgetComponent } from './shared/note-widget/note-widget.component';
import { NotesDialogManagerComponent } from './shared/notes-dialog-manager/notes-dialog-manager.component';
import { WoundTrackerComponent } from './shared/wound-tracker/wound-tracker.component';
import { MemberLevelOfFunctionTableComponent } from './shared/member-level-of-function-table/member-level-of-function-table.component';
import { DatePipe } from '@angular/common';
import { NotesCreateButtonComponent } from './shared/notes-create-button/notes-create-button.component';
import { NotesViewButtonComponent } from './shared/notes-view-button/notes-view-button.component';
import { AuthenticationService } from './core/service';

/**
 * MFE-specific default theme loader. Lazy-loads `@mynexus/theme` so the Carelon
 * preset is only fetched when the shell hasn't supplied its own theme.
 */
const loadCarelonPrimeNGConfig: PrimeNGConfigLoader = async () => {
  const { Carelon, CSS_LAYERS_ORDER } = await import('@mynexus/theme');

  return {
    ripple: false,
    theme: {
      preset: Carelon,
      options: {
        darkModeSelector: false,
        cssLayer: {
          name: 'primeng',
          order: CSS_LAYERS_ORDER
        }
      },
    }
  };
};

export function loadConfig(configurationService: ConfigurationService) {
  return () => {
    const url = `${environment.apiBaseUrl}/.well-known/client-configuration`;
    let config: IConfiguration = null;

    // Get the configuration
    configurationService.loadConfig(url).subscribe(
      {
        next: (c) => {
          config = c;
          if (!config) {
            throw new Error('Configuration data not found!');
          }
        },
        error: (error) => {
          console.error(`Error load config data: ${error}`);
        },
      });
  };
}

@NgModule({
  declarations: [
    MncNotesComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    MncNotesRoutingModule,
    CoreModule,
    FeatureToggleModule.forRoot(`${environment.apiBaseUrl}/api/`),
    SecurityModule.forRoot([]),
    ...environment.modules
  ],
  providers: [
    providePrimeNGFromShell(loadCarelonPrimeNGConfig),
    ConfigurationService,
    provideAppInitializer(() => {
      const initializerFn = (loadConfig)(inject(ConfigurationService));
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RouteInterceptor,
      multi: true
    },
    UtcToLocalPipe,
    DialogService,
    MessageService,
    DatePipe,
    UtcDateWithoutTimeZonePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MncNotesModule implements DoBootstrap {
  constructor(
    injector: Injector,
    shellService: ShellService,
    mfeService: NoteMfeService,
    private securityService: SecurityService,
    private authenticationService: AuthenticationService
  ) {
    // As part of bug 246634, discovered a race condition for some web components that have a dependency SecurityModule.
    // Permissions need to be loaded as soon as possible, even before APP_INITIALIZER. With registration, we are also instantiating
    // the components, which also triggers change detection and attempting to assess permission access too soon.
    this.configureSecurityModule();

    customElements.define(
      'mnc-notes',
      createCustomElement<any>(MncNotesComponent, {injector})
    );
    customElements.define(
      'mnc-notes-sidebar',
      createCustomElement<any>(NotesSidebarComponent, {injector})
    );
    customElements.define(
      'mnc-notes-widget',
      createCustomElement<any>(NoteWidgetComponent, {injector})
    );
    customElements.define(
      'mnc-notes-dialog-manager',
      createCustomElement<any>(NotesDialogManagerComponent, {injector})
    );
    customElements.define(
      'mnc-notes-broadcast-service',
      createCustomElement<any>(BroadcastServiceComponent, {injector})
    );
    customElements.define(
      'mnc-notes-service',
      createCustomElement<any>(NotesServiceComponent, {injector})
    );
    customElements.define(
      'mnc-notes-wound-tracker',
      createCustomElement<any>(WoundTrackerComponent, {injector})
    );
    customElements.define(
      'mnc-notes-member-level-of-function-table',
      createCustomElement<any>(MemberLevelOfFunctionTableComponent, {injector})
    );
    customElements.define(
      'mnc-notes-create-button',
      createCustomElement<any>(NotesCreateButtonComponent, {injector})
    );
    customElements.define(
      'mnc-notes-view-button',
      createCustomElement<any>(NotesViewButtonComponent, {injector})
    );

    shellService.registerMfeService('mnc-notes', mfeService);
  }

  ngDoBootstrap() {
    document.dispatchEvent(new CustomEvent('mnc-notes-loaded'));
  }

  private configureSecurityModule(): void {
    this.securityService.setUserPermissions(this.authenticationService.userPermissions.flat());

    // Legacy permissions model force us to set this false to avoid problem with empty permissions users
    this.securityService.setAllowEmptyPermission(false);
  }
}

